import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, toZonedTime } from 'date-fns-tz';
import { Loader2, Wrench, Clock, ExternalLink } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ToolUsage {
  id: string;
  tool_id: string;
  tool_name: string;
  accessed_at: string;
  result_summary: Record<string, unknown> | null;
}

interface UserToolHistoryProps {
  toolAccessId: string | null;
  fingerprint: string;
}

export const UserToolHistory = ({ toolAccessId, fingerprint }: UserToolHistoryProps) => {
  const [toolUsage, setToolUsage] = useState<ToolUsage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchToolUsage = async () => {
      setLoading(true);
      
      if (toolAccessId) {
        // Fetch by tool_access_id if available
        const { data, error } = await supabase
          .from('tool_usage')
          .select('*')
          .eq('tool_access_id', toolAccessId)
          .order('accessed_at', { ascending: false })
          .limit(20);

        if (!error && data) {
          setToolUsage(data as ToolUsage[]);
        }
      }
      
      setLoading(false);
    };

    fetchToolUsage();
  }, [toolAccessId, fingerprint]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (toolUsage.length === 0) {
    return (
      <div className="text-center py-4 text-xs text-muted-foreground">
        <Wrench className="w-4 h-4 mx-auto mb-1 opacity-50" />
        No tool usage recorded
      </div>
    );
  }

  return (
    <ScrollArea className="h-[200px]">
      <div className="space-y-2 pr-2">
        {toolUsage.map((usage) => (
          <div
            key={usage.id}
            className="p-2 rounded-lg bg-muted/50 border border-border/50"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <Wrench className="w-3 h-3 text-primary" />
                {usage.tool_name}
              </div>
              <a
                href={`/tools?tool=${usage.tool_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
              <Clock className="w-2.5 h-2.5" />
              {format(toZonedTime(new Date(usage.accessed_at), 'America/New_York'), 'MMM d, h:mm a')} EST
            </div>
            {usage.result_summary && (
              <div className="mt-1.5 text-[10px] text-muted-foreground bg-background/50 p-1.5 rounded font-mono overflow-hidden">
                {Object.entries(usage.result_summary).slice(0, 3).map(([key, value]) => (
                  <div key={key} className="truncate">
                    <span className="text-primary/70">{key}:</span>{' '}
                    {typeof value === 'string' ? value.slice(0, 30) : JSON.stringify(value).slice(0, 30)}
                    {((typeof value === 'string' && value.length > 30) || JSON.stringify(value).length > 30) && '...'}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};
